import React, { useState, useCallback, useMemo } from 'react';
import { GoogleMap, Marker, LoadScript, InfoWindow } from '@react-google-maps/api';
import { Box, CircularProgress, Typography } from '@mui/material';
import VenueDetailsModal from './VenueDetailsModal';

const containerStyle = {
  width: '100%',
  height: 'calc(100vh - 200px)',
  minHeight: '600px'
};

const MapView = ({ 
  venues = [], 
  leads = [], 
  userLocation,
  onVenueClick 
}) => {
  const [map, setMap] = useState(null);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState(null);

  // Transform venues (accounts) into markers
  const accountMarkers = useMemo(() => {
    if (!Array.isArray(venues)) return [];
    
    return venues
      .filter(venue => venue.latitude && venue.longitude)
      .map(venue => ({
        id: venue.venue_id,
        type: 'account',
        name: venue.venue_name,
        address: venue.address || venue.vicinity || '',
        latitude: parseFloat(venue.latitude),
        longitude: parseFloat(venue.longitude),
        venue: venue
      }));
  }, [venues]);

  // Transform leads into markers
  const leadMarkers = useMemo(() => {
    if (!Array.isArray(leads)) return [];
    
    return leads
      .map(lead => {
        try {
          const data = typeof lead.data === 'string' ? JSON.parse(lead.data) : lead.data;
          if (!data?.geometry?.location) return null;
          
          return {
            id: lead.id,
            type: 'lead',
            name: lead.name,
            address: lead.vicinity || '',
            latitude: data.geometry.location.lat,
            longitude: data.geometry.location.lng,
            venue: {
              ...lead,
              data: data,
              photos: typeof lead.photos === 'string' ? JSON.parse(lead.photos) : lead.photos,
              types: typeof lead.types === 'string' ? JSON.parse(lead.types) : lead.types
            }
          };
        } catch (error) {
          console.error('Error parsing lead data:', error);
          return null;
        }
      })
      .filter(marker => marker !== null);
  }, [leads]);

  // Combine all markers
  const allMarkers = useMemo(() => {
    return [...accountMarkers, ...leadMarkers];
  }, [accountMarkers, leadMarkers]);

  // Default center - use user location if available, otherwise use first marker or default
  const defaultCenter = useMemo(() => {
    if (userLocation?.lat && userLocation?.lng) {
      return {
        lat: userLocation.lat,
        lng: userLocation.lng
      };
    }
    
    if (allMarkers.length > 0) {
      return {
        lat: allMarkers[0].latitude,
        lng: allMarkers[0].longitude
      };
    }
    
    // Default to Sydney, Australia (common location for Australian businesses)
    return {
      lat: -33.8688,
      lng: 151.2093
    };
  }, [userLocation, allMarkers]);

  const onLoad = useCallback((map) => {
    setMap(map);
    setMapsLoaded(true);
    
    if (allMarkers.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      
      // Add user location to bounds if available
      if (userLocation?.lat && userLocation?.lng) {
        bounds.extend({
          lat: userLocation.lat,
          lng: userLocation.lng
        });
      }
      
      // Add all markers to bounds
      allMarkers.forEach(marker => {
        bounds.extend({
          lat: marker.latitude,
          lng: marker.longitude
        });
      });
      
      map.fitBounds(bounds);
      
      // If only one marker, set a reasonable zoom level
      if (allMarkers.length === 1) {
        const listener = map.addListener('idle', () => {
          map.setZoom(Math.min(14, map.getZoom()));
          window.google.maps.event.removeListener(listener);
        });
      }
    } else {
      // No markers, center on user location or default
      map.setCenter(defaultCenter);
      map.setZoom(userLocation ? 12 : 10);
    }
  }, [allMarkers, userLocation, defaultCenter]);

  const getMarkerIcon = useCallback((marker) => {
    if (!mapsLoaded) return null;
    
    const isSelected = selectedMarker?.id === marker.id;
    const color = marker.type === 'account' ? '#00A693' : '#FF6B6B'; // Teal for accounts, red for leads
    
    return {
      path: window.google.maps.SymbolPath.CIRCLE,
      fillColor: color,
      fillOpacity: isSelected ? 1 : 0.7,
      strokeColor: '#FFFFFF',
      strokeWeight: isSelected ? 3 : 2,
      scale: isSelected ? 10 : 8
    };
  }, [mapsLoaded, selectedMarker]);

  const handleMarkerClick = useCallback((marker) => {
    setSelectedMarker(marker);
    setSelectedVenue(marker.venue);
    setShowModal(true);
    
    if (onVenueClick) {
      onVenueClick(marker.venue);
    }
  }, [onVenueClick]);

  if (!userLocation && allMarkers.length === 0) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="600px"
        flexDirection="column"
        gap={2}
      >
        <CircularProgress sx={{ color: '#00A693' }} />
        <Typography variant="body1" color="text.secondary">
          Waiting for location permission...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height: 'calc(100vh - 200px)', minHeight: '600px', position: 'relative' }}>
      <LoadScript googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={defaultCenter}
          zoom={12}
          onLoad={onLoad}
          onUnmount={() => {
            setMap(null);
            setMapsLoaded(false);
          }}
          options={{
            mapTypeControl: true,
            streetViewControl: true,
            fullscreenControl: true,
          }}
        >
          {/* User location marker */}
          {mapsLoaded && userLocation?.lat && userLocation?.lng && (
            <Marker
              position={{
                lat: userLocation.lat,
                lng: userLocation.lng
              }}
              icon={{
                path: window.google.maps.SymbolPath.CIRCLE,
                fillColor: '#4285F4',
                fillOpacity: 1,
                strokeColor: '#FFFFFF',
                strokeWeight: 2,
                scale: 10
              }}
              title="Your Location"
            />
          )}

          {/* Venue markers */}
          {mapsLoaded && allMarkers.map((marker) => (
            <Marker
              key={`${marker.type}-${marker.id}`}
              position={{
                lat: marker.latitude,
                lng: marker.longitude
              }}
              icon={getMarkerIcon(marker)}
              onClick={() => handleMarkerClick(marker)}
              title={marker.name}
            />
          ))}

          {/* Info window for selected marker */}
          {selectedMarker && (
            <InfoWindow
              position={{
                lat: selectedMarker.latitude,
                lng: selectedMarker.longitude
              }}
              onCloseClick={() => setSelectedMarker(null)}
            >
              <div>
                <h4 style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>
                  {selectedMarker.name}
                </h4>
                <p style={{ margin: '0 0 4px 0', fontSize: '14px', color: '#666' }}>
                  {selectedMarker.address}
                </p>
                <p style={{ margin: '4px 0 0 0', fontSize: '12px' }}>
                  <span style={{ 
                    padding: '2px 8px', 
                    borderRadius: '12px', 
                    backgroundColor: selectedMarker.type === 'account' ? '#00A693' : '#FF6B6B',
                    color: 'white',
                    fontWeight: 'bold'
                  }}>
                    {selectedMarker.type === 'account' ? 'Account' : 'Lead'}
                  </span>
                </p>
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      </LoadScript>

      {/* Legend */}
      <Box
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          backgroundColor: 'white',
          padding: '12px',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          zIndex: 1
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
          Legend
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: '#4285F4',
              mr: 1
            }}
          />
          <Typography variant="caption">Your Location</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: '#00A693',
              mr: 1
            }}
          />
          <Typography variant="caption">Accounts ({accountMarkers.length})</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: '#FF6B6B',
              mr: 1
            }}
          />
          <Typography variant="caption">Leads ({leadMarkers.length})</Typography>
        </Box>
      </Box>

      {/* Venue Details Modal */}
      {selectedVenue && (
        <VenueDetailsModal
          venue={selectedVenue}
          open={showModal}
          onClose={() => {
            setShowModal(false);
            setSelectedMarker(null);
            setSelectedVenue(null);
          }}
        />
      )}
    </Box>
  );
};

export default MapView;
